import { AlertTriangle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { ResetAcquisitionDialog } from "@/components/admin/reset-acquisition-dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useOnline } from "@/hooks/use-online"
import type { ResetAcquisitionPreview } from "@/types/admin"

interface BrandDangerZoneCardProps {
  brandId: number
  brandName: string
  preview: ResetAcquisitionPreview | undefined
}

/**
 * OKS-514 — "Danger zone" card at the bottom of the admin brand detail.
 * Destructive-accent `Card` housing the irreversible "Reset acquisition"
 * action. OKS-Admin-only surface (the whole `/admin/*` tree is admin-gated);
 * the endpoint itself re-checks via `Admin::Brands::AcquisitionResetPolicy`.
 *
 * Spans the full grid width (`lg:col-span-2`) and sits last so the
 * destructive control is visually separated from the read-only detail cards.
 */
export function BrandDangerZoneCard({
  brandId,
  brandName,
  preview,
}: BrandDangerZoneCardProps) {
  const { t } = useTranslation()
  const online = useOnline()

  return (
    <Card
      className="border-destructive/50 lg:col-span-2"
      data-testid="admin-brand-danger-zone"
    >
      <CardHeader>
        <CardTitle className="text-destructive inline-flex items-center gap-2">
          <AlertTriangle className="size-4" aria-hidden="true" />
          {t("admin.brand_detail.reset.card_title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-muted-foreground max-w-prose text-sm">
          {t("admin.brand_detail.reset.card_body")}
        </p>
        <div className="shrink-0">
          <ResetAcquisitionDialog
            brandId={brandId}
            brandName={brandName}
            preview={preview}
            online={online}
          />
        </div>
      </CardContent>
    </Card>
  )
}
